import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { CheckCircleLightGlyph, ExclamationCircleLightGlyph, ExclamationTriangleLightGlyph, InfoCircleLightGlyph } from '@acast-tech/decibel-glyphs';
import T from 'prop-types';
import { cloneElement, forwardRef } from 'react';
import styled from 'styled-components';
import { black16, black2, black64, purple, purple56, purple8, green, green56, green8, red, red56, red8, yellow, yellow56, yellow8 } from '../../styles/colors';
import { createPropTypes, margin } from '../../styles/system';
import { body2 } from '../../styles/typography';
import { unit } from '../../styles/unit';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const intentStyleMap = {
  primary: {
    glyph: InfoCircleLightGlyph,
    glyphColor: purple,
    backgroundColor: purple8,
    borderColor: purple56
  },
  secondary: {
    glyph: InfoCircleLightGlyph,
    glyphColor: black64,
    backgroundColor: black2,
    borderColor: black16
  },
  success: {
    glyph: CheckCircleLightGlyph,
    glyphColor: green,
    backgroundColor: green8,
    borderColor: green56
  },
  warning: {
    glyph: ExclamationCircleLightGlyph,
    glyphColor: yellow,
    backgroundColor: yellow8,
    borderColor: yellow56
  },
  danger: {
    glyph: ExclamationTriangleLightGlyph,
    glyphColor: red,
    backgroundColor: red8,
    borderColor: red56
  }
};
const Container = /*#__PURE__*/styled.div.withConfig({
  displayName: "Container",
  componentId: "sc-1mvm2zc-0"
})(["display:flex;padding:", ";background-color:", ";border:1px solid ", ";border-radius:4px;width:100%;", ""], unit(4), props => props.backgroundColor, props => props.borderColor, margin);
const IconContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "IconContainer",
  componentId: "sc-1mvm2zc-1"
})(["flex:0 0 auto;margin-right:", ";"], unit(3));
const BodyActionContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "BodyActionContainer",
  componentId: "sc-1mvm2zc-2"
})(["display:flex;flex-direction:column;flex-grow:1;min-width:0;"]);
const Body = /*#__PURE__*/styled.div.withConfig({
  displayName: "Body",
  componentId: "sc-1mvm2zc-3"
})(["", ";white-space:pre-line;min-width:0;"], body2);
const ActionContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "ActionContainer",
  componentId: "sc-1mvm2zc-4"
})(["flex:0 0 auto;margin-top:", ";"], unit(2));

const FlashAction = props => /*#__PURE__*/_jsx(Button, Object.assign({}, props, {
  size: "xsmall",
  variant: "tertiary"
}));

const Flash = /*#__PURE__*/forwardRef((_ref, forwardedRef) => {
  let {
    intent = 'primary',
    customGlyph: CustomGlyph,
    children,
    action
  } = _ref,
      restProps = _objectWithoutPropertiesLoose(_ref, ["intent", "customGlyph", "children", "action"]);

  const _ref2 = intentStyleMap[intent] || {},
        {
    glyph,
    glyphColor
  } = _ref2,
        intentStyles = _objectWithoutPropertiesLoose(_ref2, ["glyph", "glyphColor"]);

  return /*#__PURE__*/_jsxs(Container, Object.assign({}, intentStyles, restProps, {
    ref: forwardedRef,
    children: [/*#__PURE__*/_jsx(IconContainer, {
      children: /*#__PURE__*/_jsx(Icon, {
        glyph: CustomGlyph || glyph,
        primaryColor: glyphColor
      })
    }), /*#__PURE__*/_jsxs(BodyActionContainer, {
      children: [/*#__PURE__*/_jsx(Body, {
        children: children
      }), action && /*#__PURE__*/_jsx(ActionContainer, {
        children: /*#__PURE__*/cloneElement(action, {
          intent: intent === 'secondary' ? 'default' : intent
        })
      })]
    })]
  }));
});
Flash.propTypes = Object.assign({
  intent: T.oneOf(['primary', 'secondary', 'success', 'warning', 'danger']),
  customGlyph: T.elementType,
  children: T.node,
  action: T.node
}, createPropTypes(margin.propNames));
export { Flash, FlashAction };